/**
 * First-party job-alert attribution (plan D9 — no Mailgun click tracking).
 *
 * A job link in a digest is `/jobs/<slug>?…&ja=<runId>`. The job page calls
 * `captureJobAlertClick`, which records `clicked` once per (run, job) per
 * browser session and remembers `{ runId, slug, at }` in sessionStorage. When
 * an application for that same job succeeds, the apply flow calls
 * `recordJobAlertApplied`, which records `applied` once and clears it. The
 * server only counts an engagement whose job was actually in that digest.
 *
 * Everything here is best-effort and silent: storage may be blocked, and
 * neither navigation nor the apply flow may ever wait on — or fail because
 * of — attribution. The beacon itself never throws (`recordJobAlertEngagement`).
 */

import { recordJobAlertEngagement } from '@/lib/job-alerts-api';

/** sessionStorage key holding the pending `{ runId, slug, at }`. */
export const JOB_ALERT_ATTRIBUTION_KEY = 'vhire_job_alert_attribution';

const CLICK_GUARD_PREFIX = 'vhire_job_alert_clicked:';

/** Run ids are Postgres UUIDs; anything else is a mangled link, not ours to post. */
const RUN_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** An application more than a day after the click is not credited to the email. */
const ATTRIBUTION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface JobAlertAttribution {
  runId: string;
  slug: string;
  at: number;
}

/** Runs `fn` against sessionStorage; blocked or absent storage yields `fallback`. */
function withStorage<T>(fn: (storage: Storage) => T, fallback: T): T {
  try {
    return fn(window.sessionStorage);
  } catch {
    return fallback;
  }
}

function isAttribution(value: unknown): value is JobAlertAttribution {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.runId === 'string' &&
    RUN_ID_RE.test(v.runId) &&
    typeof v.slug === 'string' &&
    typeof v.at === 'number'
  );
}

function readAttribution(): JobAlertAttribution | null {
  const raw = withStorage((s) => s.getItem(JOB_ALERT_ATTRIBUTION_KEY), null);
  if (raw === null) return null;
  let parsed: unknown = null;
  try {
    parsed = JSON.parse(raw);
  } catch {
    /* corrupt — discarded below */
  }
  if (isAttribution(parsed)) return parsed;
  withStorage((s) => s.removeItem(JOB_ALERT_ATTRIBUTION_KEY), undefined);
  return null;
}

/**
 * Job page mount. `search` is `window.location.search`. Does nothing unless the
 * URL carries a UUID-shaped `ja`.
 */
export function captureJobAlertClick(slug: string, search: string): void {
  const runId = new URLSearchParams(search).get('ja');
  if (!slug || !runId || !RUN_ID_RE.test(runId)) return;

  const attribution: JobAlertAttribution = { runId, slug, at: Date.now() };
  withStorage((s) => s.setItem(JOB_ALERT_ATTRIBUTION_KEY, JSON.stringify(attribution)), undefined);

  const guardKey = `${CLICK_GUARD_PREFIX}${runId}:${slug}`;
  if (withStorage((s) => s.getItem(guardKey), null) !== null) return;
  withStorage((s) => s.setItem(guardKey, '1'), undefined);
  recordJobAlertEngagement({ runId, jobSlug: slug, kind: 'clicked' });
}

/**
 * Apply-flow success. `jobIdentifier` is the apply route's job segment — the
 * slug the job page linked to. Fires `applied` only for the attributed job,
 * once: the attribution is cleared before the beacon goes out.
 */
export function recordJobAlertApplied(jobIdentifier: string): void {
  const attribution = readAttribution();
  if (!attribution || attribution.slug !== jobIdentifier) return;

  withStorage((s) => s.removeItem(JOB_ALERT_ATTRIBUTION_KEY), undefined);
  if (Date.now() - attribution.at > ATTRIBUTION_MAX_AGE_MS) return;
  recordJobAlertEngagement({ runId: attribution.runId, jobSlug: attribution.slug, kind: 'applied' });
}
